export default class CaptableMethods {
  constructor(companyId) {
    this.companyId = companyId;
    this.simulation =
      $_GET("simulation") == null ? null : parseInt($_GET("simulation"));
  }

  getGroupFor(receiverId, receiverType) {
    let group = trans("not_grouped");

    if (
      receiverType === peque.SHARE_RECEIVER_TYPE_STAKEHOLDER &&
      this.stakeholders[receiverId].group != null
    ) {
      group = this.stakeholders[receiverId].group;
    } else if (receiverType !== peque.SHARE_RECEIVER_TYPE_STAKEHOLDER) {
      group = trans("equity_plans");
    }

    return group;
  }

  getStakeholders() {
    return new Promise((resolve, reject) => {
      peque
        .api("stakeholders", { company: currentCompany.id })
        .then((response) => {
          if (response.error > 0) {
            return;
          }
          const list = {};
          response.result.list.forEach((entry) => {
            list[entry.id] = entry;
          });
          this.stakeholders = list;
          resolve(list);
        });
    });
  }

  getUnconvertedSum(selectedDate) {
    return new Promise((resolve, reject) => {
      peque
        .api("convertibles/unconverted_sum", {
          company: this.companyId,
          date: selectedDate,
          simulation: this.simulation,
        })
        .then((response) => {
          resolve(response.result.total);
        });
    });
  }

  getPools() {
    return new Promise((resolve, reject) => {
      peque
        .api("pools", { company: this.companyId, simulation: this.simulation })
        .then((response) => {
          if (response.error > 0) {
            return;
          }
          const list = {};
          response.result.list.forEach((entry) => {
            list[entry.id] = entry;
          });
          this.pools = list;
          resolve(list);
        });
    });
  }

  getGrants(selectedDate) {
    return new Promise((resolve, reject) => {
      peque
        .api("grants", {
          company: this.companyId,
          date: selectedDate,
          simulation: this.simulation,
          full: true,
        })
        .then((response) => {
          const list = {};

          response.result.list.forEach((entry) => {
            list[entry.id] = entry;
          });
          resolve(list);
        });
    });
  }

  getShares(selectedDate) {
    return new Promise((resolve, reject) => {
      peque
        .api("shares", {
          company: this.companyId,
          date: selectedDate,
          simulation: this.simulation,
          include_transaction_type: true,
        })
        .then((response) => {
          const list = {};
          response.result.list.forEach((entry) => {
            list[entry.id] = entry;
          });
          resolve(list);
        });
    });
  }

  validateSettings(settings) {
    if (typeof settings === "undefined") {
      settings = {};
    }
    if (typeof settings.grouped === "undefined") {
      settings.grouped = false;
    }
    return settings;
  }

  parseTransactions(valuations, shares, grants, settings) {
    const list = {};
    const counts = {
      stakeholders: {},
      valuation: 0,
      invested: 0,
      shares: 0,
      issued_shares: 0,
    };
    let nearestValuationDate = 0;

    settings = this.validateSettings(settings);

    Object.keys(valuations).forEach((valuationId) => {
      const valuation = valuations[valuationId];

      const parts = valuation.date.split("-");
      const timestamp = new Date(parts[0], parts[1] - 1, parts[2]).getTime();

      if (timestamp > nearestValuationDate && valuation.price) {
        nearestValuationDate = timestamp;
        counts.valuation = valuation.price;
      }
    });

    Object.keys(shares).forEach((shareId) => {
      const share = shares[shareId];

      if (share.receiver_type === peque.SHARE_RECEIVER_TYPE_STAKEHOLDER) {
        counts.issued_shares += share.amount;
      }

      // don't count secondary in investment
      // (as the company doesnt receive that money, so doesn't count as investment)
      if (
        share.type === peque.TRANSACTION_TYPE_TRANSFER &&
        share.investment > 0
      ) {
        counts.invested -= share.investment;
      }

      let grantId = share.grant;

      // make shares issued from stock options count
      if (
        typeof share.grant !== "undefined" &&
        share.grant > 0 &&
        typeof grants[share.grant] !== "undefined" &&
        [peque.GRANT_TYPE_STOCK_OPTIONS, peque.GRANT_TYPE_WARRANTS].includes(
          grants[share.grant].plan.grant_type
        )
      ) {
        grantId = undefined;
      }
      // Don't count stock grants as they already issue shares
      if (
        typeof share.grant !== "undefined" &&
        share.grant > 0 &&
        typeof grants[share.grant] !== "undefined" &&
        grants[share.grant].plan.grant_type === peque.GRANT_TYPE_STOCK
      ) {
        const grant = grants[share.grant];
        const poolKey = this.getKey(
          grant.plan.pool.id,
          peque.SHARE_RECEIVER_TYPE_POOL,
          settings.grouped
        );
        list[poolKey].shares[grant.plan.pool.class] -= share.amount;
        list[poolKey].sum -= share.amount;
      }

      this.parseTransaction(list, counts, settings, {
        receiverId: share.receiver_id,
        receiverType: share.receiver_type,
        class: share.class,
        amount: share.amount,
        investment: share.investment,
        grant: grantId,
        beneficiary: share.beneficiary,
      });
    });

    Object.keys(grants).forEach((grantId) => {
      const grant = grants[grantId];
      const key = this.getKey(
        grant.stakeholder.id,
        peque.SHARE_RECEIVER_TYPE_STAKEHOLDER
      );
      const poolKey = this.getKey(
        grant.plan.pool.id,
        peque.SHARE_RECEIVER_TYPE_POOL,
        settings.grouped
      );

      if (typeof list[key] === "undefined") {
        this.addEntry(
          list,
          grant.stakeholder.id,
          peque.SHARE_RECEIVER_TYPE_STAKEHOLDER
        );
      }
      if (typeof list[key].esop[grant.plan.pool.class] === "undefined") {
        list[key].esop[grant.plan.pool.class] = 0;
      }

      let assignedGrant =
        grant.leaver_type > 0 ? grant.vested_amount : grant.amount;

      if (grant.buybacks) {
        let boughtback = grant.buybacks.reduce(
          (accumulator, currentValue) => accumulator + currentValue.amount,
          0
        );
        assignedGrant -= boughtback;
        if(grant.plan.grant_type === peque.GRANT_TYPE_STOCK) {
          counts.issued_shares -= boughtback;
        }
      }

      list[key].esop[grant.plan.pool.class] += assignedGrant;
      list[key].esop_sum += assignedGrant;

      if (typeof list[poolKey] !== "undefined") {
        list[poolKey].esop_sum -= assignedGrant;
      }

      if (
        grant.plan.grant_type === peque.GRANT_TYPE_PHANTOM_SHARES ||
        ([peque.GRANT_TYPE_STOCK_OPTIONS, peque.GRANT_TYPE_WARRANTS].includes(
          grant.plan.grant_type
        ) &&
          grant.exercised === 0)
      ) {
        return;
      }

      // 0 if the share class has issuing as any vested amount will be issued as normal shares
      let consolidatedAmount =
        grant.vesting_type > 0 ? grant.vested_amount : grant.amount;

      if (grant.exercised > 0) {
        consolidatedAmount = grant.exercised;
      }

      if (typeof list[poolKey] === "undefined") {
        return;
      }
      // Don't count stock grants as they already issue shares
      if (peque.GRANT_TYPE_STOCK === grant.plan.grant_type) {
        return;
      }

      list[poolKey].shares[grant.plan.pool.class] -= consolidatedAmount;
      list[poolKey].sum -= consolidatedAmount;

      // since both pool & the issuing have been counted, we have to subtract the amount
      // to prevent showing a duplicated value
      if (
        [peque.GRANT_TYPE_STOCK_OPTIONS, peque.GRANT_TYPE_WARRANTS].includes(
          grant.plan.grant_type
        )
      ) {
        counts.shares -= consolidatedAmount;
        return;
      }

      if (consolidatedAmount <= 0 || grant.vesting_type < 1) {
        return;
      }

      this.parseTransaction(list, counts, settings, {
        receiverId: grant.stakeholder.id,
        receiverType: peque.STAKEHOLDER_TYPE_PERSON,
        class: grant.plan.pool.class,
        amount: consolidatedAmount,
        investment: 0,
        grantType: grant.plan.grant_type,
        beneficiary: grant.beneficiary ? grant.beneficiary.id : null,
      });
    });

    let employees = {
      list: {},
      stakeholders: {},
    };

    // remove stakeholders with no stake
    Object.keys(list).forEach((key) => {
      // no longer holds any stake
      if (list[key].sum === 0) {
        if (list[key].esop_sum > 0) {
          employees.list[key] = list[key];
        }

        delete list[key];

        if (key.endsWith("-1")) {
          const stakeholderId = key.split("-")[0];
          employees.stakeholders[stakeholderId] =
            counts.stakeholders[stakeholderId];
          delete counts.stakeholders[stakeholderId];
        }
      }
    });

    return {
      list,
      counts,
      employees,
    };
  }

  getKey(receiverId, receiverType, grouped) {
    return grouped
      ? this.getGroupFor(receiverId, receiverType)
      : receiverId + "-" + receiverType;
  }

  addEntry(list, receiverId, receiverType, grouped) {
    const group = this.getGroupFor(receiverId, receiverType);
    const key = this.getKey(receiverId, receiverType, grouped);
    let name;

    if (grouped) {
      name = group;
    } else {
      name =
        receiverType === peque.SHARE_RECEIVER_TYPE_STAKEHOLDER
          ? this.stakeholders[receiverId].fullName
          : this.pools[receiverId].name;
    }

    list[key] = {
      receiver_id: receiverId,
      name: name,
      type: receiverType,
      beneficiary: false,
      beneficiaries: [],
      investment: 0,
      group: group,
      shares: {},
      phantoms: {},
      esop: {},
      sum: 0,
      phantom_sum: 0,
      esop_sum: 0,
    };
  }

  parseTransaction(list, counts, settings, transaction) {
    const key = this.getKey(
      transaction.receiverId,
      transaction.receiverType,
      settings.grouped
    );
    const beneficiaryKey =
      transaction.beneficiary &&
      !settings.grouped &&
      typeof this.stakeholders[transaction.beneficiary] !== "undefined"
        ? this.getKey(
            transaction.beneficiary,
            transaction.receiverType + "-beneficiary",
            settings.grouped
          )
        : null;

    const isAGrant = typeof transaction.grantType !== "undefined";
    const comesFromAGrant =
      typeof transaction.grant !== "undefined" && transaction.grant > 0;
    const areRealShares =
      !isAGrant || [peque.GRANT_TYPE_STOCK].includes(transaction.grantType);

    if (typeof list[key] === "undefined") {
      this.addEntry(
        list,
        transaction.receiverId,
        transaction.receiverType,
        settings.grouped
      );
    }
    if (beneficiaryKey && typeof list[beneficiaryKey] === "undefined") {
      const beneficiaryName =
        this.stakeholders[transaction.beneficiary].fullName;

      if (!list[key].beneficiaries.includes(beneficiaryName)) {
        list[key].beneficiaries.push(beneficiaryName);
      }

      list[beneficiaryKey] = {
        receiver_id: transaction.receiverId,
        name:
          this.stakeholders[transaction.receiverId].fullName +
          " - " +
          beneficiaryName,
        type: transaction.receiverType,
        beneficiaries: [],
        beneficiary: true,
        investment: 0,
        group: this.getGroupFor(
          transaction.beneficiary,
          transaction.receiverType
        ),
        shares: {},
        phantoms: {},
        sum: 0,
        phantom_sum: 0,
      };
    }

    if (typeof list[key].shares[transaction.class] === "undefined") {
      list[key].shares[transaction.class] = 0;
      list[key].phantoms[transaction.class] = 0;
    }
    if (
      beneficiaryKey &&
      typeof list[beneficiaryKey].shares[transaction.class] === "undefined"
    ) {
      list[beneficiaryKey].shares[transaction.class] = 0;
      list[beneficiaryKey].phantoms[transaction.class] = 0;
    }

    if (areRealShares) {
      list[key].shares[transaction.class] += transaction.amount;
      list[key].sum += transaction.amount;

      if (transaction.receiverType === peque.SHARE_RECEIVER_TYPE_POOL) {
        list[key].esop_sum += transaction.amount;
      }

      if (beneficiaryKey) {
        list[beneficiaryKey].shares[transaction.class] += transaction.amount;
        list[beneficiaryKey].sum += transaction.amount;
      }
    } else {
      list[key].phantoms[transaction.class] += transaction.amount;
      list[key].phantom_sum += transaction.amount;
    }

    list[key].investment += transaction.investment;
    if (beneficiaryKey) {
      list[beneficiaryKey].investment += transaction.investment;
    }

    if (!isAGrant && !comesFromAGrant) {
      counts.shares += transaction.amount;
    }

    counts.invested += transaction.investment;

    if (transaction.receiverType === peque.SHARE_RECEIVER_TYPE_STAKEHOLDER) {
      counts.stakeholders[transaction.receiverId] = true;
    }
  }
}

window.CaptableMethods = CaptableMethods;
